'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var React = require('react');
var reactQuery = require('react-query');
var nthHookLoma = require('@narvar/nth-hook-loma');
var nthHookGlobalSettings = require('@narvar/nth-hook-global-settings');
var nthBitErrorBoundary = require('@narvar/nth-bit-error-boundary');
var useLomaClient = require('./useLomaClient.js');
var NthProviders = require('./NthProviders.js');
var ReturnsProvider = require('./ReturnsProvider.js');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

var React__default = /*#__PURE__*/_interopDefaultLegacy(React);

const ReturnsConfigContext = /*#__PURE__*/React.createContext(null);
const useReturnsConfig = () => {
  const value = React.useContext(ReturnsConfigContext);
  if (!value) throw new Error('Expected ReturnsConfigContext.Provider');
  return value;
};

/**
 * This component loads default configuration values from nth global settings
 * and then provides the resolved configuration via the `useReturnsConfig` hook.
 */
const ReturnsConfigProviderCore = _ref => {
  let {
    children,
    noflakeUrl,
    handleError,
    isHipaaCompliant,
    csrSignInErrorCode,
    csrSignInUrl,
    lomaUrl,
    returnsHeadlessUrl,
    moniker,
    versionName,
    localeCountry,
    localeLanguage,
    includeCurrency,
    prefill,
    localConfig,
    token,
    getToken,
    overrideCode
  } = _ref;
  const {
    loading,
    error,
    globalSettings: {
      defaultCountry = 'US',
      defaultLanguage = 'en',
      defaultVersionName,
      hipaaCompliant = false,
      returnsWorkflowActiveVersion,
      returnsWorkflowDefaultVersion
    }
  } = nthHookGlobalSettings.useGlobalSettingsX();
  if (error) throw error;
  const value = React.useMemo(() => {
    if (loading) return null;
    if (typeof defaultVersionName !== 'string') {
      throw new Error(`Expected global setting to be string: defaultVersionName`);
    }
    if (typeof returnsWorkflowDefaultVersion !== 'string') {
      throw new Error(`Expected global setting to be string: returnsWorkflowDefaultVersion`);
    }
    return {
      noflakeUrl,
      handleError,
      isHipaaCompliant: isHipaaCompliant ?? hipaaCompliant ?? false,
      csrSignInErrorCode,
      csrSignInUrl,
      lomaUrl,
      returnsHeadlessUrl,
      moniker,
      versionName: versionName ?? returnsWorkflowActiveVersion ?? returnsWorkflowDefaultVersion,
      themeVersionName: defaultVersionName,
      localeCountry: localeCountry ?? defaultCountry ?? 'US',
      localeLanguage: localeLanguage ?? defaultLanguage ?? 'en',
      includeCurrency,
      prefill,
      token,
      localConfig,
      getToken,
      overrideCode
    };
  }, [loading, defaultCountry, defaultLanguage, defaultVersionName, returnsWorkflowActiveVersion, returnsWorkflowDefaultVersion, noflakeUrl, handleError, hipaaCompliant, isHipaaCompliant, csrSignInErrorCode, csrSignInUrl, lomaUrl, returnsHeadlessUrl, moniker, versionName, localeCountry, localeLanguage, includeCurrency, prefill, token, localConfig, getToken, overrideCode]);
  if (!value) return null;
  return /*#__PURE__*/React__default["default"].createElement(ReturnsConfigContext.Provider, {
    value: value
  }, children);
};
const ReturnsGlobalSettingsProvider = _ref2 => {
  let {
    localConfig,
    children,
    ...props
  } = _ref2;
  return localConfig !== null && localConfig !== void 0 && localConfig.queryGlobalSettings ? /*#__PURE__*/React__default["default"].createElement(nthHookGlobalSettings.LocalGlobalSettingsConfigProvider, {
    queryGlobalSettings: () => ({
      loading: false,
      globalSettings: localConfig.queryGlobalSettings()
    })
  }, /*#__PURE__*/React__default["default"].createElement(nthHookGlobalSettings.LocalGlobalSettingsProvider, props, children)) : /*#__PURE__*/React__default["default"].createElement(nthHookGlobalSettings.GlobalSettingsProvider, props, children);
};
const ReturnsConfigProvider = props => {
  const queryClient = React.useMemo(() => new reactQuery.QueryClient(), []);

  // create an apollo client for loma
  const {
    lomaUrl,
    handleError
  } = props;
  const {
    error,
    client: lomaClient
  } = useLomaClient["default"]({
    lomaUrl,
    handleError
  });
  if (error) throw error;
  if (!lomaClient) return null;
  const {
    children,
    moniker,
    localConfig
  } = props;
  return /*#__PURE__*/React__default["default"].createElement(reactQuery.QueryClientProvider, {
    client: queryClient
  }, /*#__PURE__*/React__default["default"].createElement(nthHookLoma.LomaClientProvider, {
    client: lomaClient
  }, /*#__PURE__*/React__default["default"].createElement(nthHookGlobalSettings.LomaGlobalSettingsConfigProvider, {
    retailerMoniker: moniker
  }, /*#__PURE__*/React__default["default"].createElement(ReturnsGlobalSettingsProvider, {
    globalSettingsRequested: `
              defaultCountry
              defaultLanguage
              defaultVersionName
              hipaaCompliant
              returnsWorkflowActiveVersion
              returnsWorkflowDefaultVersion
            `,
    localConfig: localConfig
  }, /*#__PURE__*/React__default["default"].createElement(ReturnsConfigProviderCore, props, /*#__PURE__*/React__default["default"].createElement(NthProviders["default"], null, /*#__PURE__*/React__default["default"].createElement(nthBitErrorBoundary.ErrorBoundaryProvider, {
    handleError: handleError
  }, /*#__PURE__*/React__default["default"].createElement(ReturnsProvider["default"], null, children))))))));
};

exports.ReturnsConfigProviderCore = ReturnsConfigProviderCore;
exports["default"] = ReturnsConfigProvider;
exports.useReturnsConfig = useReturnsConfig;
